import { Component, inject } from '@angular/core';
import { LabelComponent } from '../../form/label/lable';
import { CheckboxComponent } from '../../form/input/checkbox';
import { ButtonComponent } from '../../ui/button/button';
import { InputFieldComponent } from '../../form/input/input-field';
import { Router, RouterModule } from '@angular/router';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../shared/services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { passwordComplexityValidator, passwordComplexityMessage } from '../../../shared/validators/password.validator';
import { properEmailValidator } from '../../../shared/validators/email.validator';
import { ConfirmationPopup } from '../../ui/confirmation-popup/confirmation-popup';

@Component({
  selector: 'app-register-merchant-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    InputFieldComponent,
    CheckboxComponent,
    ButtonComponent,
    LabelComponent,
    ConfirmationPopup
  ],
  templateUrl: './register-merchant-form.html'
})
export class RegisterMerchantForm {
  private fb = inject(FormBuilder);

  showPassword = false;
  isLoading = false;
  showVerifyEmailPrompt = false;

  constructor(private authService: AuthService, private toastr: ToastrService, private router: Router) { }

  form = this.fb.group({
    uname: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(30)]],
    fname: ['', [Validators.required, Validators.maxLength(50)]],
    email: ['', [Validators.required, properEmailValidator]],
    password: ['', [
      Validators.required,
      Validators.minLength(8),
      Validators.maxLength(64),
      passwordComplexityValidator
    ]],
    terms: [false, Validators.requiredTrue]
  });

  get f() {
    return this.form.controls;
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  fieldError(name: keyof typeof this.form.controls): string {
    const ctrl = this.f[name] as AbstractControl;

    if (!ctrl.invalid || !(ctrl.touched || ctrl.dirty)) return '';

    const e = ctrl.errors!;

    if (e['required'] || e['requiredTrue']) return this.requiredMsg(name);
    if (e['minlength'])
      return `Minimum ${e['minlength'].requiredLength} characters required`;
    if (e['maxlength'])
      return `Maximum ${e['maxlength'].requiredLength} characters allowed`;
    if (e['email']) return 'Invalid email format';
    if (e['needsUppercase'] || e['needsNumber'] || e['hasSpace']) return passwordComplexityMessage(e);

    return 'Invalid field';
  }

  private requiredMsg(name: string): string {
    const map: Record<string, string> = {
      uname: 'User name is required',
      fname: 'Full name is required',
      email: 'Email is required',
      password: 'Password is required',
      terms: 'You must accept the Terms and Conditions'
    };
    return map[name] ?? 'This field is required';
  }

  onTermsChange(checked: boolean) {
    this.form.get('terms')!.setValue(checked);
    this.form.get('terms')!.markAsTouched();
    this.form.get('terms')!.markAsDirty();
  }

  onSignUp() {
    Object.values(this.form.controls).forEach(ctrl => {
      ctrl.markAsTouched();
      ctrl.markAsDirty();
    });

    if (this.form.invalid) return;

    this.isLoading = true;

    const { uname, fname, email, password } = this.form.getRawValue();

    this.authService.signupMerchant({
      email: email!.trim(),
      userName: uname!.trim(),
      displayName: fname!.trim(),
      password: password!,
    })
      .subscribe({
        next: (res: any) => {
          this.isLoading = false;
          this.form.reset();
          this.showVerifyEmailPrompt = true;
        },
        error: (err: any) => {
          this.isLoading = false;
          this.toastr.error(err?.error?.message ?? 'Sign up failed!', 'Error');
        }
      });
  }

  goToLoginFromVerifyPrompt(): void {
    this.showVerifyEmailPrompt = false;
    this.router.navigate(['/login']);
  }

  goToHomeFromVerifyPrompt(): void {
    this.showVerifyEmailPrompt = false;
    this.router.navigate(['/']);
  }
}
